import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import {
    Container,
    Row,
    Col,
    Card,
    CardBody,
} from "reactstrap";

import Api from "../../components/helpers/Api";
import { SeoHelper } from "../../components/helpers/SeoHelper";
import SeoNoIndex from "../../components/helpers/SeoNoIndex";
import { RiMusic2Line, RiBook2Line, RiBookOpenLine } from "react-icons/ri";
import { buildScriptureSEO } from "../../utils/buildScriptureSEO";
import ScripturePlanningBridge from "../../components/seo/ScripturePlanningBridge";
import KstTrackLinks from "../../components/kst/KstTrackLinks";

export default function ScriptureSongsPage() {

    const { book, chapter, verse, range } = useParams();

    const [books, setBooks] = useState([]);
    const [popular, setPopular] = useState([]);
    const [expandedGroups, setExpandedGroups] = useState({});
    const [data, setData] = useState(null);
    const [related, setRelated] = useState([]);

    const GROUP_VISIBLE_LIMIT = 5;

    // ==========================
    // ✅ SEO (DETERMINISTIC)
    // ==========================
    let verseStart = null;
    let verseEnd = null;

    if (range) {
        const parts = range.split("-");
        verseStart = parts[0];
        verseEnd = parts[1] || parts[0];
    } else if (verse) {
        verseStart = verse;
    }

    const seo = buildScriptureSEO({
        book,
        chapter,
        verseStart,
        verseEnd
    });

    // ==========================
    // DATA LOAD
    // ==========================
    useEffect(() => {
        Api.get("/api/testament/books")
            .then(res => setBooks(res.data));
    }, []);

    useEffect(() => {
        Api.get("/api/scripture-songs/popular")
            .then(res => setPopular(res.data));
    }, []);

    useEffect(() => {

        let url = `/api/scripture-songs/${book}/${chapter}`;

        if (verse) url = `/api/scripture-songs/${book}/${chapter}/${verse}`;
        if (range) url = `/api/scripture-songs/${book}/${chapter}/${range}`;

        Api.get(url)
            .then(res => setData(res.data));

    }, [book, chapter, verse, range]);

    useEffect(() => {
        const scriptureReference =
            `${formatBookName(book)} ${chapter}` +
            (verse || range ? `:${verse || range}` : "");

        if (typeof window.gtag === 'function') {
            window.gtag('event', 'view_scripture', {
                book,
                chapter,
                verse: verse || range || null,
                scripture_reference: scriptureReference
            });
        }
    }, [book, chapter, verse, range]);

    useEffect(() => {

        const currentPath =
            `/songs/${book}/${chapter}` +
            (range ? `/${range}` : verse ? `/${verse}` : "");

        Api.get("/api/scripture-songs")
            .then(res => {

                const items = res.data || [];

                const filtered = items.filter(p => p.url !== currentPath);
                const index = items.findIndex(p => p.url === currentPath);

                let nearby = [];

                if (index !== -1) {
                    const start = Math.max(0, index - 2);
                    const end = Math.min(items.length, index + 3);

                    nearby = items
                        .slice(start, end)
                        .filter(p => p.url !== currentPath)
                        .slice(0, 2);
                }

                const remaining = filtered.filter(
                    p => !nearby.some(n => n.url === p.url)
                );

                const random = [...remaining]
                    .sort(() => 0.5 - Math.random())
                    .slice(0, 2);

                setRelated([...nearby, ...random]);

            });

    }, [book, chapter, verse, range]);

    useEffect(() => {

        if (!data) return;

        const scriptureReference = `${formatBookName(book)} ${chapter}:${verse || range}`;

        if (typeof window.gtag === 'function') {
            window.gtag('event', 'view_song_list', {
                scripture_reference: scriptureReference,
                content_type: 'verse',
                source: 'browse'
            });
        }

    }, [data, book, chapter, verse, range]);

    // ==========================
    // HELPERS
    // ==========================
    function formatBookName(value) {
        return value
            .replace(/-/g, " ")
            .split(" ")
            .filter(Boolean)
            .map(p => /^\d+$/.test(p) ? p : p.charAt(0).toUpperCase() + p.slice(1))
            .join(" ");
    }

    function toggleGroup(group) {
        setExpandedGroups(prev => ({
            ...prev,
            [group]: !prev[group]
        }));
    }

    const bookMeta = books.find(b => b.slug === book);
    const maxChapters = bookMeta?.chapters;
    const nextBook = bookMeta?.nextBook;

    // Determine whether this page should be indexed.
    // Pages with no songs (invalid or empty verse references) must not be indexed.
    const hasSongs = data?.songs?.length > 0;
    const shouldIndex = !data || hasSongs; // while loading, don't make a decision yet

    return (
        <>
            {/* SEO: noindex when the page has loaded but has no songs */}
            {shouldIndex ? (
                <SeoHelper
                    uri={seo.canonical}
                    title={seo.title}
                    description={seo.description}
                />
            ) : (
                <SeoNoIndex
                    uri={seo.canonical}
                    description={seo.description}
                />
            )}

            {/* ==========================
                SAFE RENDER GUARD
               ========================== */}
            {!data || !bookMeta ? (
                <div className="text-center mt-5">Loading...</div>
            ) : (() => {

                const bookDisplay = formatBookName(data.book);

                const passageText =
                    `${bookDisplay} ${data.chapter}` +
                    (data.verseFrom ? `:${data.verseFrom}` : "") +
                    (data.verseTo && data.verseTo !== data.verseFrom ? `–${data.verseTo}` : "");

                const chapterNumber = parseInt(data.chapter, 10);


                return (
                    <section className="section section-lg">

                        <Container>

                            <Row className="justify-content-center">

                                <Col lg="9" xl="8">

                                    <h1 className="display-4 text-center text-primary mb-3">
                                        {seo.heading}
                                    </h1>

                                    <p className="lead text-center text-muted mb-3">
                                        Discover worship songs inspired by {passageText}.
                                    </p>

                                    {data.songs?.length > 0 ? (
                                        <Card className="mb-4">
                                            <CardBody>

                                                <h2 className="h4 mb-3">
                                                    <RiMusic2Line size={22} style={{ marginRight: "8px" }} />
                                                    Recommended Worship Songs
                                                </h2>

                                                <ul className="twb-grid">
                                                    {data.songs.map(song => (
                                                        <li key={song.id}>
                                                            <div className="twb-next-step">
                                                                <div className="twb-next-step-icon">
                                                                    <RiMusic2Line size={20} />
                                                                </div>
                                                                <div className="twb-next-step-text">
                                                                    {song.title}
                                                                    <KstTrackLinks kstTracks={song.kstTracks} seo />
                                                                </div>
                                                            </div>
                                                        </li>
                                                    ))}
                                                </ul>

                                            </CardBody>
                                        </Card>
                                    ) : (
                                        <p className="text-muted">
                                            There are currently no songs for this passage.
                                        </p>
                                    )}

                                    <Card className="mb-4">
                                        <CardBody>

                                            <h3 className="h5 mb-3">
                                                <RiBook2Line size={20} /> Related Bible Passages
                                            </h3>

                                            <ul className="twb-grid">
                                                {related.map(r => (
                                                    <li key={r.url}>
                                                        <Link to={r.url} className="twb-next-step">
                                                            <div className="twb-next-step-icon">
                                                                <RiBookOpenLine size={20} />
                                                            </div>
                                                            <div className="twb-next-step-text">
                                                                {r.title}
                                                            </div>
                                                        </Link>
                                                    </li>
                                                ))}
                                            </ul>

                                        </CardBody>
                                    </Card>

                                    <ScripturePlanningBridge />

                                </Col>

                            </Row>

                        </Container>

                    </section>
                );

            })()}
        </>
    );
}
